/// 一覧条件の自動保存・復元ポリシー (FSD: features)。
///
/// 保存する内容・供給元・復元優先順位・スクロール制御という「判断」はここに閉じる。
/// 値の受理/正規化は shared/lib/list-view-state (純粋コーデック) に委譲する。
///
/// 保存モデル (schema version 1):
///   - localStorage: `{ version, query }`      … 適用済みの検索/絞り込み/並び順
///   - sessionStorage: `{ version, query, page, scrollY }` … タブ内の作業位置
///   `page` とスクロール位置は **常に session 封筒フィールド** であり query には入れない。
///
/// createListViewPersistence が両一覧ページ (firms / lawyers) 共通の orchestrator。

import { tick } from 'svelte';

import {
  isPlainRecord,
  readClampedPositiveInteger,
} from '@shared/lib/list-view-state';

export const LIST_VIEW_STATE_SCHEMA_VERSION = 1;

const MAX_PAGE = 1_000_000;
const MAX_SCROLL_Y = 100_000_000;
const SCROLL_DEBOUNCE_MS = 100;

export interface ListViewStateIdentity {
  userId: string;
  orgId: string | null;
}

export type ListViewSaveMode = 'full' | 'session';

export interface RestoredListViewState<Query> {
  query: Query;
  source: 'session' | 'local' | 'default';
  page: number;
  scrollY: number | null;
}

interface ListViewEnvelope {
  version: number;
  query: Record<string, unknown>;
  page?: unknown;
  scrollY?: unknown;
}

interface RestoreListViewStateOptions<Query> {
  identity: ListViewStateIdentity;
  screen: string;
  defaults: Query;
  validate: (value: unknown) => Partial<Query>;
  localStorage: Storage;
  sessionStorage: Storage;
}

interface SaveListViewStateOptions<Query> {
  identity: ListViewStateIdentity;
  screen: string;
  query: Query;
  page: number;
  scrollY: number;
  serialize: (query: Query) => Record<string, unknown>;
  mode: ListViewSaveMode;
  localStorage: Storage;
  sessionStorage: Storage;
}

export function buildListViewStateStorageKey(
  identity: ListViewStateIdentity,
  screen: string,
): string {
  const orgId = identity.orgId ?? 'null';
  return [
    'lpcrm',
    'list-view-state',
    `v${LIST_VIEW_STATE_SCHEMA_VERSION}`,
    encodeURIComponent(orgId),
    encodeURIComponent(identity.userId),
    screen,
  ].join(':');
}

/**
 * 復元優先順位: 有効な session > 有効な local > 既定値。
 *
 * session 封筒だけが `page` とスクロール位置を供給する。 local からの復元は
 * `page = 1` / `scrollY = null`。 version 一致でも query が record でない封筒は
 * 無効として次の候補へフォールバックする。
 */
export function restoreListViewState<Query extends { page?: number }>({
  identity,
  screen,
  defaults,
  validate,
  localStorage,
  sessionStorage,
}: RestoreListViewStateOptions<Query>): RestoredListViewState<Query> {
  const key = buildListViewStateStorageKey(identity, screen);

  const session = readEnvelope(sessionStorage, key);
  if (session) {
    const page = readClampedPositiveInteger(session.page, MAX_PAGE) ?? 1;
    const query = mergeValidated(defaults, validate(session.query));
    return {
      query: { ...query, page },
      source: 'session',
      page,
      scrollY: validateScrollY(session.scrollY),
    };
  }

  const local = readEnvelope(localStorage, key);
  if (local) {
    const query = mergeValidated(defaults, validate(local.query));
    return {
      query: { ...query, page: 1 },
      source: 'local',
      page: 1,
      scrollY: null,
    };
  }

  return {
    query: defaults,
    source: 'default',
    page: defaults.page ?? 1,
    scrollY: null,
  };
}

/**
 * 保存モード:
 *   - 'full': localStorage `{version, query}` と session `{version, query, page, scrollY}` の両方。
 *   - 'session': session 封筒のみ (localStorage は触らない)。
 *
 * `serialize(query)` は常に page を含めない。 page/scrollY は session 封筒フィールド。
 */
export function saveListViewState<Query>({
  identity,
  screen,
  query,
  page,
  scrollY,
  serialize,
  mode,
  localStorage,
  sessionStorage,
}: SaveListViewStateOptions<Query>): void {
  const key = buildListViewStateStorageKey(identity, screen);
  const serialized = serialize(query);

  if (mode === 'full') {
    writeEnvelope(localStorage, key, {
      version: LIST_VIEW_STATE_SCHEMA_VERSION,
      query: serialized,
    });
  }

  writeSessionEnvelope(sessionStorage, key, {
    version: LIST_VIEW_STATE_SCHEMA_VERSION,
    query: serialized,
    page: readClampedPositiveInteger(page, MAX_PAGE) ?? 1,
    scrollY: normalizeScrollY(scrollY),
  });
}

export interface ListViewListState {
  total: number;
  page_size: number;
}

export interface ListViewPersistenceOptions<Query extends { page?: number }> {
  screen: string;
  identity: ListViewStateIdentity | null;
  createDefaults: () => Query;
  validate: (value: unknown) => Partial<Query>;
  serialize: (query: Query) => Record<string, unknown>;
  getQuery: () => Query;
  setQuery: (query: Query) => void;
  syncSearchInput: (query: Query) => void;
  refresh: () => Promise<void>;
  getListState: () => ListViewListState | null;
  getListError: () => string | null;
}

export interface ListViewPersistence {
  initialize: () => Promise<void>;
  onQueryApplied: () => void;
  onPageChanged: () => void;
  dispose: () => void;
}

/**
 * 一覧ページ共通の永続化 orchestrator。 getter/callback 経由で state を読み書きする
 * ため、 この module 内では runes を持たない (ページ側の `$state` に閉じる)。
 */
export function createListViewPersistence<Query extends { page?: number }>(
  options: ListViewPersistenceOptions<Query>,
): ListViewPersistence {
  const { identity } = options;
  let disposed = false;
  let listenersAttached = false;
  let scrollSaveTimer: number | null = null;
  // dispose / onQueryApplied で increment。 遅延スクロール復元が別ルート/操作後に
  // 発火しても、 epoch が変わっていれば何もしない (stale offset ジャンプ防止)。
  let scrollEpoch = 0;
  // 予定しているスクロール復元先 (session 復元は保存位置、 local/default 復元は
  // 先頭 0)。 initialize が fetch 前に確定させ、 復元実行時 / ユーザースクロール /
  // 条件適用 / ページ移動 / 破棄で null に戻す。 rAF 復元コールバックはこの値が
  // 復元予定値と一致しなければ中止する (ユーザー操作が優先)。
  let pendingScrollY: number | null = null;

  function currentPage(): number {
    return options.getQuery().page ?? 1;
  }

  function persist(mode: ListViewSaveMode, page: number, scrollY: number): void {
    if (!identity) return;
    try {
      saveListViewState({
        identity,
        screen: options.screen,
        query: options.getQuery(),
        page,
        scrollY,
        serialize: options.serialize,
        mode,
        localStorage: window.localStorage,
        sessionStorage: window.sessionStorage,
      });
    } catch {
      // window.localStorage / sessionStorage プロパティ参照自体がブラウザに
      // ブロックされることがある。 一覧は既定値のまま使える。
    }
  }

  // 復元予定値は initialize が fetch 前に pendingScrollY へ設定済み。 ここでは epoch を
  // 捕捉し、 DOM 反映後 (double rAF) に復元する。 dispose / 条件適用 / ページ移動で epoch が
  // 変わった場合、 または fetch 中〜復元前のユーザースクロールで pendingScrollY が
  // 復元予定値から外れた場合は中止する (ユーザー操作が復元に優先する)。
  function scheduleScrollRestore(scrollY: number): void {
    const epoch = scrollEpoch;
    void tick().then(() => {
      window.requestAnimationFrame(() =>
        window.requestAnimationFrame(() => {
          if (disposed || epoch !== scrollEpoch || pendingScrollY !== scrollY)
            return;
          window.scrollTo({ top: scrollY, left: 0 });
          if (pendingScrollY === scrollY) pendingScrollY = null;
        }),
      );
    });
  }

  // ユーザーのスクロール入力 (wheel / touch) は発生時点で復元予定を取り消す
  // (ユーザー操作が復元に優先する)。 取り消しを scroll の debounce タイマー内
  // だけで行うと、 入力後タイマー発火前に rAF 復元 / pagehide が来た場合に
  // ユーザー操作が復元予定値に負けてしまう。
  function handleUserScrollIntent(): void {
    pendingScrollY = null;
  }

  // raw `scroll` イベントはユーザー入力と無関係にも発火する (fetch 反映で文書
  // 高さが縮み viewport が clamp される等) ため、 復元予定の取り消しは従来
  // どおり debounce 後に限る。 これにより clamp では復元が生き残る (double rAF
  // が debounce より先に完了する)。 scrollbar ドラッグ等 scroll しか発火しない
  // 操作には debounce 窓が残るが、 従来挙動からの退行はない。
  function handleScroll(): void {
    if (scrollSaveTimer !== null) window.clearTimeout(scrollSaveTimer);
    scrollSaveTimer = window.setTimeout(() => {
      scrollSaveTimer = null;
      pendingScrollY = null;
      persist('session', currentPage(), window.scrollY);
    }, SCROLL_DEBOUNCE_MS);
  }

  function handlePageHide(): void {
    persist('session', currentPage(), pendingScrollY ?? window.scrollY);
  }

  function attachListeners(): void {
    if (listenersAttached || disposed) return;
    listenersAttached = true;
    window.addEventListener('scroll', handleScroll, { passive: true });
    window.addEventListener('wheel', handleUserScrollIntent, { passive: true });
    window.addEventListener('touchmove', handleUserScrollIntent, {
      passive: true,
    });
    window.addEventListener('pagehide', handlePageHide);
  }

  function removeListeners(): void {
    if (!listenersAttached) return;
    listenersAttached = false;
    window.removeEventListener('scroll', handleScroll);
    window.removeEventListener('wheel', handleUserScrollIntent);
    window.removeEventListener('touchmove', handleUserScrollIntent);
    window.removeEventListener('pagehide', handlePageHide);
  }

  async function initialize(): Promise<void> {
    if (!identity) {
      // 永続化は identity 依存。 一覧の初回取得だけ行い、 保存/復元はしない。
      await options.refresh();
      return;
    }

    const defaults = options.createDefaults();
    let restored: RestoredListViewState<Query>;
    try {
      restored = restoreListViewState({
        identity,
        screen: options.screen,
        defaults,
        validate: options.validate,
        localStorage: window.localStorage,
        sessionStorage: window.sessionStorage,
      });
    } catch {
      restored = {
        query: defaults,
        source: 'default',
        page: defaults.page ?? 1,
        scrollY: null,
      };
    }

    options.setQuery(restored.query);
    // 検索欄は復元時に一度だけ同期する。 以降 (fetch 中の入力・ページ補正) は触らない。
    options.syncSearchInput(restored.query);

    const intendedScrollY =
      restored.source === 'session' ? (restored.scrollY ?? 0) : 0;
    const restoredPage = restored.page;

    if (restored.source !== 'session') {
      // 永続設定 / 既定値からの復元は page 1・画面上部から開始する。 router は
      // SPA 遷移でスクロールをリセットしないため、 ここで戻さないと遷移前の
      // スクロール位置のまま表示され、 その位置が初期化末尾の保存で session
      // 封筒に固定されてしまう。
      window.scrollTo({ top: 0, left: 0 });
    }

    // 復元予定値 (session: 保存位置 / local・default: 先頭 0) を fetch 前に確定させ、
    // リスナも fetch 前に接続する。 これにより初回 fetch 中 (stale 行が描画されている間)
    // のユーザースクロールが handleScroll 経由で session 保存され、 かつ復元を取り消せる。
    // fetch 中の離脱 (dispose / pagehide) でも pendingScrollY に開始/保存済み位置が
    // 残るため、 遷移前の残留 window.scrollY で上書き喪失しない。
    pendingScrollY = intendedScrollY;
    attachListeners();

    const initializationEpoch = scrollEpoch;
    await options.refresh();
    if (disposed) return;

    // 初期取得中に検索・ソート・ページ移動が行われた場合、以降の復元処理は
    // その操作より古い。復元ページの補正・保存・スクロールを実行しない。
    if (initializationEpoch !== scrollEpoch) return;

    const listState = options.getListState();
    if (options.getListError() !== null || listState === null) {
      // store がエラー / 未反映のときは stale な total に対してページ補正しない。
      // ユーザースクロールで復元が取り消された場合は現在位置を保存する。
      persist('session', restoredPage, pendingScrollY ?? window.scrollY);
      return;
    }

    const lastPage = Math.max(
      1,
      Math.ceil(listState.total / listState.page_size),
    );
    if ((restored.query.page ?? 1) > lastPage) {
      options.setQuery({ ...options.getQuery(), page: lastPage });
      // 内容が変わるため復元をキャンセルして先頭へ戻す (先頭に留まる)。
      pendingScrollY = null;
      window.scrollTo({ top: 0, left: 0 });
      persist('session', lastPage, 0);
      await options.refresh();
      return;
    }

    if (restored.source === 'session') {
      scheduleScrollRestore(intendedScrollY);
    }
    // 復元予定値 (pendingScrollY) を保存する。 fetch 中にユーザースクロールで
    // 取り消された場合はその現在位置 (window.scrollY) を保存する。
    persist('session', restoredPage, pendingScrollY ?? window.scrollY);
  }

  function onQueryApplied(): void {
    scrollEpoch += 1;
    pendingScrollY = null;
    window.scrollTo({ top: 0, left: 0 });
    persist('full', currentPage(), 0);
  }

  function onPageChanged(): void {
    scrollEpoch += 1;
    pendingScrollY = null;
    window.scrollTo({ top: 0, left: 0 });
    // ページ移動はタブ内の作業位置であり、別タブと共有する条件を更新しない。
    persist('session', currentPage(), 0);
  }

  function dispose(): void {
    disposed = true;
    scrollEpoch += 1;
    if (scrollSaveTimer !== null) {
      window.clearTimeout(scrollSaveTimer);
      scrollSaveTimer = null;
    }
    removeListeners();
    if (identity) {
      persist('session', currentPage(), pendingScrollY ?? window.scrollY);
    }
  }

  return { initialize, onQueryApplied, onPageChanged, dispose };
}

function readEnvelope(storage: Storage, key: string): ListViewEnvelope | null {
  try {
    const raw = storage.getItem(key);
    if (raw === null) return null;
    const parsed: unknown = JSON.parse(raw);
    if (!isPlainRecord(parsed)) return null;
    if (parsed.version !== LIST_VIEW_STATE_SCHEMA_VERSION) return null;
    // version 一致でも query が record でなければ無効 → 次候補へフォールバック。
    if (!isPlainRecord(parsed.query)) return null;
    return {
      version: LIST_VIEW_STATE_SCHEMA_VERSION,
      query: parsed.query,
      page: parsed.page,
      scrollY: parsed.scrollY,
    };
  } catch {
    return null;
  }
}

function writeEnvelope(
  storage: Storage,
  key: string,
  envelope: Record<string, unknown>,
): void {
  try {
    storage.setItem(key, JSON.stringify(envelope));
  } catch {
    // Storage が無効 / 満杯でも一覧は既定値で使える。
  }
}

function writeSessionEnvelope(
  storage: Storage,
  key: string,
  envelope: Record<string, unknown>,
): void {
  try {
    storage.setItem(key, JSON.stringify(envelope));
  } catch {
    // 書き込み失敗時に古い session 封筒が残ると、 より新しい localStorage を
    // 上回って復元されてしまう (torn write)。 best-effort で key を消す。
    try {
      storage.removeItem(key);
    } catch {
      // Storage 自体が使えない。 復元は次候補 / 既定値へフォールバックする。
    }
  }
}

function mergeValidated<Query>(
  defaults: Query,
  validated: Partial<Query>,
): Query {
  const definedEntries = Object.entries(
    validated as Record<string, unknown>,
  ).filter(([, value]) => value !== undefined);
  return {
    ...defaults,
    ...Object.fromEntries(definedEntries),
  };
}

function validateScrollY(value: unknown): number | null {
  if (typeof value !== 'number' || !Number.isFinite(value) || value < 0) {
    return null;
  }
  return normalizeScrollY(value);
}

function normalizeScrollY(value: number): number {
  if (!Number.isFinite(value) || value < 0) return 0;
  return Math.min(Math.floor(value), MAX_SCROLL_Y);
}
